/**
 * Steuerruecklagen SEO page configurations — DE/AT/CH
 * All country-specific strings, FAQs, legal text and calculator options.
 * Tax calculation logic lives in the backend (single source of truth).
 */
import React from 'react';

export const STEUERRUECKLAGEN_CONFIG = {
  DE: {
    country: "DE",
    hreflangKey: "de",
    path: "/steuerruecklagen-freelancer-deutschland",
    title: (year) => `Steuerrücklagen-Rechner Deutschland ${year} – ESt, Soli & KV`,
    metaDescription: () => `Wie viel % zurücklegen? Rechner mit Einkommensteuer, Soli, Gewerbesteuer & KV-Beiträgen. Vorauszahlungstermine für Freelancer in Deutschland.`,
    ogUrl: "https://ivoliq.io/steuerruecklagen-freelancer-deutschland",
    ogLocale: "de_DE",
    canonicalUrl: "https://ivoliq.io/steuerruecklagen-freelancer-deutschland",
    breadcrumbLast: "Deutschland",
    howToName: "Steuerrücklagen als Freelancer in Deutschland berechnen",
    howToDescription: "So berechnest du deine Steuerrücklagen als Freelancer in Deutschland – mit Einkommensteuer, Solidaritätszuschlag und Umsatzsteuer.",
    howToSteps: [
      { name: "Bruttoeinkommen eintragen", text: "Erfasse dein jährliches Bruttoeinkommen als Freelancer in Deutschland." },
      { name: "Betriebsausgaben abziehen", text: "Ziehe deine absetzbaren Betriebsausgaben ab: Büro, Equipment, Software." },
      { name: "Steuerrücklage ablesen", text: "Der Rechner zeigt dir deine empfohlene monatliche Rücklage inkl. ESt, Soli und USt." },
    ],

    // Hero
    heroEyebrow: (year) => `Steuerrücklagen-Rechner Deutschland ${year}`,
    heroTitle: "Wie viel Steuern musst du als Freelancer in Deutschland zurücklegen?",
    heroSubtitle: (year) => `Gewinn eintragen, Rücklage pro Monat ablesen. Einkommensteuer, Solidaritätszuschlag, Gewerbesteuer und Krankenversicherung – gerechnet mit den Sätzen für ${year}.`,
    heroBullets: [
      "Progressiver ESt-Tarif §32a EStG statt pauschaler Prozentsatz",
      "Gewerbesteuer nur, wenn du gewerblich tätig bist (Freibetrag 24.500 €)",
      "Ergebnis als Link teilbar – z. B. an deinen Steuerberater",
    ],
    trustBadges: [
      { label: "DSGVO-konform", icon: "Shield" },
      { label: "GoBD-konform", icon: "Check" },
      { label: "Kostenlos", icon: "Euro" },
    ],

    // Why important
    whyImportantTitle: "Warum Steuerrücklagen für Freelancer so wichtig sind",
    whyImportantParagraphs: [
      <>Als Freelancer in Deutschland zahlst du deine Steuern nicht automatisch über die Lohnabrechnung. <strong>Du bist selbst dafür verantwortlich</strong>, genug Geld für Einkommensteuer, Solidaritätszuschlag und ggf. Gewerbesteuer zurückzulegen.</>,
      <>Das Problem: Viele Freelancer unterschätzen die Steuerlast und stehen am Jahresende vor einer <strong>unangenehmen Nachzahlung</strong>. In Deutschland können Steuern und Sozialabgaben <strong>35-45% deines Gewinns</strong> ausmachen.</>,
    ],

    // How much section
    howMuchTitle: "Wie viel muss ich als Freelancer in Deutschland zurücklegen?",
    taxRates: [
      { label: "Einkommensteuer", rate: "14-45%", description: "Progressiver Tarif nach §32a EStG", color: "emerald" },
      { label: "Solidaritätszuschlag", rate: "0-5,5%", description: "5,5% der ESt (mit Freigrenze & Milderungszone)", color: "blue" },
      { label: "Gewerbesteuer", rate: "0-17%", description: "Nur Gewerbetreibende, Freibetrag 24.500 €", color: "orange" },
      { label: "Sozialversicherung", rate: "~20%", description: "KV + PV (RV freiwillig für Freiberufler)", color: "purple" },
    ],
    taxSummary: "Faustregel: Lege 35-45% deines Gewinns zurück. Mit dem Rechner oben bekommst du eine genauere Schätzung.",

    // Calculator
    calcTitle: (year) => `Steuerrechner Deutschland ${year}`,
    calcRevenueLabel: "Jahresgewinn (Netto-Einnahmen)",
    calcExpensesLabel: "Betriebsausgaben (Netto)",
    calcOptions: [
      { id: "is_gewerblich", label: "Gewerbliche Tätigkeit (nicht freiberuflich)", field: "is_gewerblich" },
      { id: "include_social_security", label: "Sozialversicherung einbeziehen (Schätzung)", field: "include_social_security", defaultChecked: true },
    ],
    calcResultTitle: "Deine Steuerberechnung für Deutschland:",
    calcResultFields: [
      { key: "taxable_income", label: "Zu versteuerndes Einkommen" },
      { key: "income_tax", label: "Einkommensteuer" },
      { key: "solidarity_surcharge", label: "Solidaritätszuschlag" },
      { key: "trade_tax", label: "Gewerbesteuer", showIf: "is_gewerblich" },
      { key: "social_security", label: "Sozialversicherung (Schätzung)", showIf: "include_social_security" },
    ],
    calcDisclaimerItems: [
      "Familienstand, Kinder, Splittingtarif",
      "Kirchensteuer",
      "Individuelle Abzüge und Freibeträge",
      "Vorauszahlungen",
    ],
    calcDisclaimerConditional: { showIf: "is_gewerblich", text: "§35 EStG Anrechnung der Gewerbesteuer auf Einkommensteuer" },

    // Länderspezifische Vertiefung
    extraSections: [
      {
        id: "krankenversicherung", tocLabel: "KV & Rentenversicherung",
        title: "Der unterschätzte Posten: Kranken- und Rentenversicherung",
        paragraphs: [
          <>Anders als Angestellte trägst du als Freelancer den <strong>vollen Beitrag zur Krankenversicherung</strong> selbst. In der GKV richtet sich der Beitrag nach deinem Einkommen (allgemeiner Beitragssatz 14,6 % plus Zusatzbeitrag der Kasse, plus Pflegeversicherung), bei der PKV nach Tarif und Alter. Diese Beträge sind keine Steuer, gehören aber zwingend in deine monatliche Rücklage.</>,
          <>Die <strong>Rentenversicherung ist für die meisten Freiberufler freiwillig</strong> – Ausnahme: rentenversicherungspflichtige Selbständige wie Lehrer, Pflegekräfte oder Handwerker. Wer freiwillig einzahlt oder privat vorsorgt, sollte auch dafür einen festen Prozentsatz vom Gewinn reservieren.</>,
        ],
        callout: { variant: "info", title: "So rechnet der Rechner", text: "Die Option „Sozialversicherung einbeziehen“ schätzt KV + PV pauschal mit rund 20 % des Gewinns (gedeckelt durch die Beitragsbemessungsgrenze). Den exakten Beitrag nennt dir deine Krankenkasse." },
      },
      {
        id: "gewerbesteuer", tocLabel: "Freiberuflich vs. gewerblich",
        title: "Freiberuflich oder gewerblich? Das entscheidet über die Gewerbesteuer",
        paragraphs: [
          <>Freiberufler nach <strong>§18 EStG</strong> (z. B. Entwickler mit ingenieurähnlicher Tätigkeit, Designer, Texter, Berater) zahlen <strong>keine Gewerbesteuer</strong>. Wer gewerblich tätig ist – etwa Handel, Vermittlung oder Agenturleistungen mit Personal – zahlt Gewerbesteuer ab einem Gewerbeertrag von 24.500 €.</>,
          <>Die Gewerbesteuer wird nach <strong>§35 EStG auf die Einkommensteuer angerechnet</strong> (Faktor 4,0 des Steuermessbetrags). Bei Hebesätzen bis etwa 400 % ist sie damit für Einzelunternehmer weitgehend neutral – bei höheren Hebesätzen wie in München (490 %) bleibt eine echte Mehrbelastung.</>,
        ],
      },
    ],

    // ivoliq section
    ivoliqTitle: "Steuerrücklagen mit ivoliq automatisch berechnen",
    ivoliqSubtitle: "Schluss mit Excel-Schätzungen: ivoliq rechnet nach jeder Rechnung neu – nach deutschem Steuerrecht.",
    mistakesTitle: "Die 4 häufigsten Rücklagen-Fehler in Deutschland",
    ivoliqSteps: [
      { title: "Rechnung erstellen", description: "Erstelle deine Rechnung in ivoliq. Die App weiß sofort: Wie viel Umsatz, wie viel deutsche Steuern." },
      { title: "Automatische Berechnung", description: "ivoliq berechnet live: Einkommensteuer, Soli, Umsatzsteuer, Gewerbesteuer, Sozialversicherung – alles nach deutschem Steuerrecht." },
      { title: "Rücklagen-Dashboard", description: "Sieh auf einen Blick: Wie viel musst du zurücklegen? Wann sind Vorauszahlungen fällig?" },
    ],
    ivoliqFeatures: [
      "Automatische Berechnung nach deutschem Steuerrecht",
      "GoBD-konforme Archivierung",
      "EÜR-fertige Aufbereitung",
      "Export für Steuerberater und ELSTER",
    ],

    // Vorauszahlung section
    vorauszahlungTitle: "Steuervorauszahlungen in Deutschland",
    vorauszahlungIntro: [
      "Das Finanzamt fordert oft quartalsweise Vorauszahlungen – basierend auf deinem Vorjahresergebnis. Diese kommen zusätzlich zur Jahressteuererklärung und können dich kalt erwischen, wenn du nicht vorbereitet bist.",
      <><strong>Die Termine sind fix:</strong> 10. März, 10. Juni, 10. September, 10. Dezember. Wichtig: Du kannst die Vorauszahlungen anpassen lassen, wenn sich dein Einkommen geändert hat. Aber bilde trotzdem Rücklagen – BEVOR die Frist kommt, nicht erst danach.</>,
    ],
    vorauszahlungQuarters: [
      { label: "Q1", date: "10. März", note: "ESt-Vorauszahlung" },
      { label: "Q2", date: "10. Juni", note: "ESt-Vorauszahlung" },
      { label: "Q3", date: "10. September", note: "ESt-Vorauszahlung" },
      { label: "Q4", date: "10. Dezember", note: "ESt-Vorauszahlung" },
    ],

    // Mistakes section
    donts: [
      { title: "Keine Rücklagen bilden", desc: "Das Finanzamt kennt kein Pardon – Steuerschulden bleiben bestehen" },
      { title: "Zu wenig zurücklegen", desc: "35-45% sind realistisch in Deutschland, nicht 10%" },
      { title: "Vorauszahlungen vergessen", desc: "Säumniszuschläge von 1% pro Monat plus 6% Zinsen" },
      { title: "Kleinunternehmergrenze überschreiten", desc: "Plötzlich USt-pflichtig – alle Rechnungen müssen korrigiert werden" },
    ],
    dos: [
      { title: "35-45% automatisch berechnen", desc: "Mit ivoliq siehst du nach jeder Rechnung die nötigen Rücklagen" },
      { title: "Quartalsweise Vorauszahlungen einplanen", desc: "März, Juni, September, Dezember – immer am 10. des Monats" },
      { title: "GoBD-konform dokumentieren", desc: "Alle Belege digital und rechtssicher archivieren" },
      { title: "Steuerberater für Jahreserklärung nutzen", desc: "Laufende Buchhaltung mit ivoliq, Jahresabschluss vom Profi" },
    ],

    // FAQ
    faqTitle: "Häufig gestellte Fragen zu Steuerrücklagen in Deutschland",
    faqs: [
      { question: "Wie viel Steuern muss ich als Freelancer in Deutschland zahlen?", answer: "Das hängt von deinem Einkommen ab, aber typischerweise 35-45% deines Gewinns. Das umfasst Einkommensteuer (progressiv 14-45%), Solidaritätszuschlag (5,5%), Umsatzsteuer (19%), Gewerbesteuer und Sozialversicherung (~20%)." },
      { question: "Wie berechne ich Steuerrücklagen in Deutschland?", answer: "Nimm deinen Gewinn (Umsatz minus Ausgaben) und multipliziere mit dem Steuersatz (35-45%). Oder nutze unseren Rechner oben auf der Seite oder ivoliq – das macht es automatisch." },
      { question: "Wann muss ich in Deutschland Steuern vorauszahlen?", answer: "Quartalsweise: 10. März, 10. Juni, 10. September, 10. Dezember." },
      { question: "Muss ich als Freiberufler Gewerbesteuer zahlen?", answer: "Nein, Freiberufler (§18 EStG) zahlen keine Gewerbesteuer. Nur Gewerbetreibende, ab 24.500 € Freibetrag." },
      { question: "Was passiert bei verspäteten Steuervorauszahlungen?", answer: "Säumniszuschlag von 1% pro Monat plus 6% Zinsen pro Jahr." },
      { question: "Brauche ich als Freelancer einen Steuerberater?", answer: "Für die Jahressteuererklärung: empfohlen. Für die laufende Buchhaltung: nicht nötig mit ivoliq." },
      { question: "Hilft ivoliq bei der Umsatzsteuer-Voranmeldung?", answer: "Ja! ivoliq berechnet deine Umsatzsteuer automatisch und bereitet alle Daten GoBD-konform auf." },
    ],

    // Internal links
    relatedCalculators: [
      { to: "/steuerruecklagen-freelancer", label: "Steuerrücklagen Übersicht" },
      { to: "/steuerruecklagen-freelancer-oesterreich", label: "Steuerrücklagen Österreich" },
      { to: "/steuerruecklagen-freelancer-schweiz", label: "Steuerrücklagen Schweiz" },
    ],
    relatedPages: [
      { to: "/buchhaltung-freelancer-deutschland", label: "Buchhaltung Deutschland" },
      { to: "/rechnungsvorlage-freelancer", label: "Rechnungsvorlagen" },
    ],

    // Final CTA
    finalCtaTitle: "Nie wieder Steuerschock in Deutschland",
    finalCtaSubtitle: "Berechne deine Steuerrücklagen automatisch mit ivoliq – nach deutschem Steuerrecht.",
    finalTrustLine: (year) => `✓ DSGVO-konform • ✓ GoBD-konform • ✓ Aktuelle Steuersätze ${year}`,
    finalTrustBadges: [
      { label: "DSGVO-konform", icon: "Shield" },
      { label: "GoBD-konform", icon: "Check" },
    ],
    finalTrustBadgeDynamic: (year) => ({ label: `Aktuelle Steuersätze ${year}`, icon: "Calculator" }),
  },

  AT: {
    country: "AT",
    hreflangKey: "at",
    path: "/steuerruecklagen-freelancer-oesterreich",
    title: (year) => `Steuerrücklagen-Rechner Österreich ${year} – SVS & ESt`,
    metaDescription: () => `Wie viel % zurücklegen? Rechner mit SVS-Beiträgen, ESt-Vorauszahlung & Gewinnfreibetrag. Fristen und Tipps für Freelancer in Österreich.`,
    ogUrl: "https://ivoliq.io/steuerruecklagen-freelancer-oesterreich",
    ogLocale: "de_AT",
    canonicalUrl: "https://ivoliq.io/steuerruecklagen-freelancer-oesterreich",
    breadcrumbLast: "Österreich",
    howToName: "Steuerrücklagen als Freelancer in Österreich berechnen",
    howToDescription: "So berechnest du deine Steuerrücklagen als Freelancer in Österreich – mit ESt, SVS und Gewinnfreibetrag.",
    howToSteps: [
      { name: "Bruttoeinkommen eintragen", text: "Erfasse dein jährliches Bruttoeinkommen als Freelancer in Österreich." },
      { name: "Betriebsausgaben abziehen", text: "Ziehe deine absetzbaren Betriebsausgaben ab." },
      { name: "Steuerrücklage ablesen", text: "Der Rechner zeigt dir deine empfohlene Rücklage inkl. ESt, SVS und Gewinnfreibetrag." },
    ],
    heroEyebrow: (year) => `Steuerrücklagen-Rechner Österreich ${year}`,
    heroTitle: "Wie viel Prozent musst du als Freelancer in Österreich zurücklegen?",
    heroSubtitle: (year) => `Gewinn eintragen, Rücklage pro Monat ablesen. Einkommensteuer nach Stufentarif, SVS-Beiträge und Gewinnfreibetrag – gerechnet mit den Werten für ${year}.`,
    heroBullets: [
      "SVS-Beiträge (Pension, Kranken, Unfall) sind mit drin – der größte Brocken im ersten Jahr",
      "Gewinnfreibetrag §10 EStG wird automatisch abgezogen",
      "Ergebnis als Link teilbar – z. B. an deinen Steuerberater",
    ],
    trustBadges: [
      { label: "DSGVO-konform", icon: "Shield" },
      { label: "Österreich-optimiert", icon: "Check" },
      { label: "Kostenlos", icon: "Euro" },
    ],
    whyImportantTitle: "Warum Steuerrücklagen für Freelancer in Österreich so wichtig sind",
    whyImportantParagraphs: [
      <>Als Freelancer in Österreich zahlst du deine Steuern nicht automatisch über die Lohnabrechnung. <strong>Du bist selbst dafür verantwortlich</strong>, genug für Einkommensteuer und SVS-Beiträge zurückzulegen.</>,
      <>Das Problem: Viele Freelancer unterschätzen die Steuerlast. In Österreich können Steuern und SVS-Beiträge <strong>40-50% deines Gewinns</strong> ausmachen – abhängig vom Gewinnfreibetrag und SVS-Optionen.</>,
    ],
    howMuchTitle: "Wie viel muss ich als Freelancer in Österreich zurücklegen?",
    taxRates: [
      { label: "Einkommensteuer", rate: "0-55%", description: "Progressiver Stufentarif (steuerfrei bis 13.539 € im Jahr 2026)", color: "emerald" },
      { label: "SVS-Beiträge", rate: "~27%", description: "Pensions-, Kranken- und Unfallversicherung", color: "blue" },
      { label: "Gewinnfreibetrag", rate: "-15%", description: "15% Grundfreibetrag auf ersten 33.000 €", color: "orange" },
    ],
    taxSummary: "Faustregel: Lege 40-50% deines Gewinns zurück. Mit dem Rechner oben bekommst du eine genauere Schätzung.",
    calcTitle: (year) => `Steuerrechner Österreich ${year}`,
    calcRevenueLabel: "Jahresgewinn (Netto-Einnahmen)",
    calcExpensesLabel: "Betriebsausgaben (Netto)",
    calcOptions: [
      { id: "include_social_security", label: "SVS-Beiträge einbeziehen", field: "include_social_security", defaultChecked: true },
    ],
    calcResultTitle: "Deine Steuerberechnung für Österreich:",
    calcResultFields: [
      { key: "taxable_income", label: "Gewinn" },
      { key: "profit_allowance", label: "Gewinnfreibetrag (Abzug)" },
      { key: "income_tax", label: "Einkommensteuer" },
      { key: "social_security", label: "SVS-Beiträge", showIf: "include_social_security" },
    ],
    calcDisclaimerItems: ["Familienstand, Kinder, Alleinverdiener-/Alleinerzieherabsetzbetrag", "Kirchenbeitrag", "Individuelle Sonderausgaben", "Kleinunternehmerregelung"],
    calcDisclaimerConditional: null,
    extraSections: [
      {
        id: "svs-nachbemessung", tocLabel: "SVS-Nachbemessung",
        title: "Die SVS-Falle: Vorläufige Beiträge und Nachbemessung",
        paragraphs: [
          <>In den ersten drei Kalenderjahren schreibt dir die SVS nur <strong>vorläufige Beiträge auf Basis der Mindestbeitragsgrundlage</strong> vor, weil dein tatsächlicher Gewinn noch nicht feststeht. Sobald der Einkommensteuerbescheid vorliegt, werden <strong>Pensions- und Krankenversicherung (18,5 % + 6,8 %) rückwirkend auf den tatsächlichen Gewinn nachbemessen</strong>; nur die Selbständigenvorsorge (1,53 %) wird nicht nachbemessen. Einzige Ausnahme: Für Gewerbetreibende (nicht für Neue Selbständige/Freiberufler) bleibt die KV-Beitragsgrundlage in den ersten zwei Kalenderjahren fix. Bei 50.000 € Gewinn können das trotzdem mehrere tausend Euro auf einmal sein, meist im dritten oder vierten Jahr.</>,
          <>Deshalb gilt: Rechne von Anfang an mit rund <strong>27 % SVS-Anteil auf den Gewinn</strong> (plus fixe Unfallversicherung von 12,95 € pro Monat) und lege die Differenz zu deinen vorläufigen Vorschreibungen sofort zur Seite. Wer will, kann bei der SVS eine <strong>freiwillige Hinaufsetzung</strong> der Beitragsgrundlage beantragen – dann kommt die Nachzahlung gar nicht erst zustande.</>,
        ],
        callout: { variant: "warning", title: "Achtung Doppel-Effekt", text: "Im Nachbemessungsjahr zahlst du laufende Beiträge PLUS Nachzahlung für das Vorjahr. Wer nur die Vorschreibung budgetiert, wird hier regelmäßig überrascht." },
      },
      {
        id: "gewinnfreibetrag", tocLabel: "Gewinnfreibetrag & KU",
        title: "Gewinnfreibetrag und Kleinunternehmer-Grenze richtig nutzen",
        paragraphs: [
          <>Der <strong>Grundfreibetrag von 15 % auf die ersten 33.000 € Gewinn</strong> steht dir automatisch zu – ohne Investition, ohne Antrag. Darüber hinaus gibt es den <strong>investitionsbedingten Gewinnfreibetrag</strong>, wenn du in begünstigte Wirtschaftsgüter oder bestimmte Wertpapiere investierst. Der Rechner zieht den Grundfreibetrag bereits ab.</>,
          <>Für die <strong>Umsatzsteuer</strong> gilt die Kleinunternehmerregelung nach § 6 Abs. 1 Z 27 UStG bis 55.000 € Jahresumsatz: Du stellst Rechnungen ohne USt und legst dafür auch keine USt zurück – ziehst aber auch keine Vorsteuer ab. Wer die Grenze überschreitet, muss ab dem Zeitpunkt USt verrechnen und quartalsweise oder monatlich per UVA abführen.</>,
        ],
      },
    ],
    ivoliqTitle: "SVS, ESt und Rücklage – live aus deinen Rechnungen",
    ivoliqSubtitle: "ivoliq rechnet nach österreichischem Steuerrecht und zeigt dir nach jeder Honorarnote, was wirklich dir gehört.",
    mistakesTitle: "Die 4 häufigsten Rücklagen-Fehler in Österreich",
    ivoliqSteps: [
      { title: "Rechnung erstellen", description: "Erstelle deine Rechnung in ivoliq. Die App berechnet sofort deine österreichischen Steuern." },
      { title: "Automatische Berechnung", description: "ivoliq berechnet live: Einkommensteuer, SVS-Beiträge, Gewinnfreibetrag – alles nach österreichischem Steuerrecht." },
      { title: "Rücklagen-Dashboard", description: "Sieh auf einen Blick: Wie viel musst du zurücklegen? Wann kommen SVS-Nachzahlungen?" },
    ],
    ivoliqFeatures: ["Automatische Berechnung nach österreichischem Steuerrecht", "SVS-Beiträge kalkuliert", "E/A-Rechnung-fertige Aufbereitung", "Export für Steuerberater und FinanzOnline"],
    vorauszahlungTitle: "Steuervorauszahlungen in Österreich",
    vorauszahlungIntro: [
      "Das Finanzamt Österreich fordert quartalsweise Vorauszahlungen – basierend auf dem Vorjahresergebnis. Zusätzlich kommen SVS-Beiträge quartalsweise.",
      <><strong>ESt-Termine:</strong> 15. Februar, 15. Mai, 15. August, 15. November. SVS-Beiträge quartalsweise. Mit ivoliq hast du alle Termine und Beträge im Blick.</>,
    ],
    vorauszahlungQuarters: [
      { label: "Q1", date: "15. Februar", note: "ESt-Vorauszahlung" },
      { label: "Q2", date: "15. Mai", note: "ESt-Vorauszahlung" },
      { label: "Q3", date: "15. August", note: "ESt-Vorauszahlung" },
      { label: "Q4", date: "15. November", note: "ESt-Vorauszahlung" },
    ],
    donts: [
      { title: "Keine Rücklagen bilden", desc: "SVS-Nachzahlungen können überraschend hoch ausfallen" },
      { title: "SVS unterschätzen", desc: "SVS-Beiträge machen ~27% des Gewinns aus" },
      { title: "Gewinnfreibetrag ignorieren", desc: "15% Grundfreibetrag verschenken ist bares Geld" },
      { title: "Vorauszahlungen vergessen", desc: "Säumniszuschläge und Zinsen beim Finanzamt" },
    ],
    dos: [
      { title: "40-50% automatisch berechnen", desc: "Mit ivoliq siehst du nach jeder Rechnung die nötigen Rücklagen" },
      { title: "SVS-Beiträge einplanen", desc: "Quartalsweise SVS-Beiträge einkalkulieren" },
      { title: "Gewinnfreibetrag nutzen", desc: "15% Grundfreibetrag automatisch berücksichtigen" },
      { title: "Steuerberater für Jahreserklärung", desc: "Laufende Buchhaltung mit ivoliq, Jahresabschluss vom Profi" },
      { title: "Investitionen 2026 gezielt planen", desc: "Der investitionsbedingte Gewinnfreibetrag mit Wertpapieren ist letztmalig für Wirtschaftsjahre nutzbar, die vor dem 1.1.2027 beginnen – 2027 bis 2029 zählen nur Sachinvestitionen. Dazu kommt der Investitionsfreibetrag (§ 11 EStG, 10 % bzw. 15 % für Öko-Investitionen). Wer 2026 investiert, senkt Steuer und Rücklage spürbar." },
    ],
    faqTitle: "Häufig gestellte Fragen zu Steuerrücklagen in Österreich",
    faqs: [
      { question: "Wie viel Steuern muss ich als Freelancer in Österreich zahlen?", answer: "Typischerweise 40-50% deines Gewinns. Das umfasst Einkommensteuer (0-55%), SVS-Beiträge (~27%) abzüglich Gewinnfreibetrag (15%)." },
      { question: "Was ist der Gewinnfreibetrag?", answer: "15% Grundfreibetrag auf die ersten 33.000 € Gewinn. Darüber hinaus bis zu 13% investitionsbedingt." },
      { question: "Wann muss ich in Österreich Steuern vorauszahlen?", answer: "Quartalsweise: 15. Februar, 15. Mai, 15. August, 15. November." },
      { question: "Muss ich SVS-Beiträge zahlen?", answer: "Ja, als Freelancer bist du SVS-pflichtig. Die Beiträge umfassen Pensions-, Kranken- und Unfallversicherung." },
      { question: "Was ist die SVS-Nachbemessung?", answer: "In den ersten drei Kalenderjahren zahlst du vorläufige SVS-Beiträge auf Basis der Mindestbeitragsgrundlage. Liegt der Einkommensteuerbescheid vor, rechnet die SVS Pensions- und Krankenversicherung auf den tatsächlichen Gewinn nach und fordert die Differenz rückwirkend – zusätzlich zu den laufenden Beiträgen. Nur die Selbständigenvorsorge (1,53 %) wird nicht nachbemessen; die KV-Fixregel der ersten zwei Jahre gilt ausschließlich für Gewerbetreibende. Lege deshalb von Beginn an rund 27 % des Gewinns für die SVS zurück." },
      { question: "Ab wann bin ich in Österreich umsatzsteuerpflichtig?", answer: "Die Kleinunternehmerregelung (§ 6 Abs. 1 Z 27 UStG) gilt bis 55.000 € Jahresumsatz. Darüber musst du Umsatzsteuer verrechnen und per UVA abführen." },
      { question: "Hilft ivoliq bei der SVS-Berechnung?", answer: "Ja! ivoliq berechnet deine SVS-Beiträge automatisch und hilft dir, die nötigen Rücklagen zu bilden." },
    ],
    relatedCalculators: [
      { to: "/steuerruecklagen-freelancer", label: "Steuerrücklagen Übersicht" },
      { to: "/steuerruecklagen-freelancer-deutschland", label: "Steuerrücklagen Deutschland" },
      { to: "/steuerruecklagen-freelancer-schweiz", label: "Steuerrücklagen Schweiz" },
    ],
    relatedPages: [
      { to: "/buchhaltung-freelancer-oesterreich", label: "Buchhaltung Österreich" },
      { to: "/rechnungsvorlage-freelancer", label: "Rechnungsvorlagen" },
    ],
    finalCtaTitle: "Nie wieder Steuerschock in Österreich",
    finalCtaSubtitle: "Berechne deine Steuerrücklagen automatisch mit ivoliq – nach österreichischem Steuerrecht.",
    finalTrustLine: (year) => `✓ DSGVO-konform • ✓ Österreich-optimiert • ✓ Aktuelle Steuersätze ${year}`,
    finalTrustBadges: [
      { label: "DSGVO-konform", icon: "Shield" },
      { label: "Österreich-optimiert", icon: "Check" },
    ],
    finalTrustBadgeDynamic: (year) => ({ label: `Aktuelle Steuersätze ${year}`, icon: "Calculator" }),
  },

  CH: {
    country: "CH",
    hreflangKey: "ch",
    path: "/steuerruecklagen-freelancer-schweiz",
    title: (year) => `Steuerrücklagen-Rechner Schweiz ${year} – AHV & Steuern`,
    metaDescription: () => `Wie viel % zurücklegen? Rechner mit Bundes- und Kantonssteuer, AHV/IV/EO und Kantonsauswahl. Akonto & MWST-Grenze für Selbständige.`,
    ogUrl: "https://ivoliq.io/steuerruecklagen-freelancer-schweiz",
    ogLocale: "de_CH",
    canonicalUrl: "https://ivoliq.io/steuerruecklagen-freelancer-schweiz",
    breadcrumbLast: "Schweiz",
    howToName: "Steuerrücklagen als Freelancer in der Schweiz berechnen",
    howToDescription: "So berechnest du deine Steuerrücklagen als Freelancer in der Schweiz – mit Bundessteuer, Kantonssteuer und AHV.",
    howToSteps: [
      { name: "Bruttoeinkommen eintragen", text: "Erfasse dein jährliches Bruttoeinkommen als Freelancer in der Schweiz." },
      { name: "Kanton wählen", text: "Wähle deinen Kanton – die Steuersätze variieren stark." },
      { name: "Steuerrücklage ablesen", text: "Der Rechner zeigt dir Bundessteuer, Kantonssteuer und AHV." },
    ],
    heroEyebrow: (year) => `Steuerrücklagen-Rechner Schweiz ${year}`,
    heroTitle: "Wie viel musst du als Selbständiger in der Schweiz für Steuern und AHV zurücklegen?",
    heroSubtitle: (year) => `Reingewinn und Kanton eintragen, Rücklage pro Monat ablesen. Direkte Bundessteuer, Kantonssteuer und AHV/IV/EO – gerechnet mit den Werten für ${year}.`,
    heroBullets: [
      "26 Kantone, 26 Steuersätze – der Rechner berücksichtigt deinen Wohnkanton",
      "AHV/IV/EO für Selbständigerwerbende inkl. sinkender Beitragsskala",
      "Ergebnis als Link teilbar – z. B. an deinen Treuhänder",
    ],
    trustBadges: [
      { label: "Swiss QR-Bill", icon: "QrCode" },
      { label: "Schweiz-optimiert", icon: "Shield" },
      { label: "Kostenlos", icon: "Euro" },
    ],
    whyImportantTitle: "Warum Steuerrücklagen für Freelancer in der Schweiz so wichtig sind",
    whyImportantParagraphs: [
      <>Als Freelancer in der Schweiz zahlst du deine Steuern nicht automatisch. <strong>Du bist selbst dafür verantwortlich</strong>, genug für Bundessteuer, Kantonssteuer und AHV zurückzulegen.</>,
      <>Die Herausforderung: Die Steuersätze variieren stark je nach Kanton. In der Schweiz können Steuern und AHV <strong>25-40% deines Gewinns</strong> ausmachen.</>,
    ],
    howMuchTitle: "Wie viel muss ich als Freelancer in der Schweiz zurücklegen?",
    taxRates: [
      { label: "Bundessteuer", rate: "0-11,5%", description: "Direkte Bundessteuer (progressiv)", color: "emerald" },
      { label: "Kantonssteuer", rate: "5-35%", description: "Variiert stark je nach Kanton und Gemeinde", color: "blue" },
      { label: "AHV/IV/EO", rate: "~10%", description: "AHV/IV/EO für Selbständige (sinkende Skala unter 60'500 CHF)", color: "orange" },
    ],
    taxSummary: "Faustregel: Lege 25-40% deines Gewinns zurück (je nach Kanton). Mit dem Rechner oben bekommst du eine genauere Schätzung.",
    calcTitle: (year) => `Steuerrechner Schweiz ${year}`,
    calcRevenueLabel: "Jahresgewinn (Netto-Einnahmen) in CHF",
    calcExpensesLabel: "Betriebsausgaben (Netto) in CHF",
    calcOptions: [
      { id: "include_social_security", label: "AHV/IV/EO-Beiträge einbeziehen", field: "include_social_security", defaultChecked: true },
    ],
    calcHasCantonSelect: true,
    calcResultTitle: "Deine Steuerberechnung für die Schweiz:",
    calcResultFields: [
      { key: "taxable_income", label: "Zu versteuerndes Einkommen" },
      { key: "federal_tax", label: "Bundessteuer" },
      { key: "cantonal_tax", label: "Kantonssteuer" },
      { key: "social_security", label: "AHV/IV/EO-Beiträge", showIf: "include_social_security" },
    ],
    calcDisclaimerItems: ["Gemeindesteuer (variiert)", "Kirchensteuer", "BVG (2. Säule)", "Individuelle Abzüge"],
    calcDisclaimerConditional: null,
    extraSections: [
      {
        id: "ahv-ausgleichskasse", tocLabel: "AHV & Ausgleichskasse",
        title: "AHV/IV/EO: Anmeldung bei der Ausgleichskasse und Akonto-Beiträge",
        paragraphs: [
          <>Als Selbständigerwerbende/r meldest du dich bei der <strong>kantonalen Ausgleichskasse</strong> an. Diese erhebt die AHV/IV/EO-Beiträge zunächst <strong>provisorisch (Akonto)</strong> auf Basis deines geschätzten Einkommens. Die <strong>definitive Abrechnung</strong> folgt erst, wenn die Steuerbehörde dein Einkommen gemeldet hat – oft zwei Jahre später, inklusive Verzugszins bei zu tiefer Schätzung.</>,
          <>Der Beitragssatz liegt bei rund <strong>10 % des Reineinkommens</strong>, mit einer sinkenden Skala bei tieferen Einkommen. Anders als Angestellte gibt es keinen Arbeitgeberanteil – du trägst alles selbst. Melde steigende Einkommen deshalb proaktiv der Ausgleichskasse, damit die Akonto-Rechnungen mitwachsen.</>,
        ],
        callout: { variant: "info", title: "Zweite und dritte Säule", text: "Die berufliche Vorsorge (BVG) ist für Selbständige freiwillig. Einzahlungen in die Säule 3a (grosser Beitrag ohne Pensionskasse) sind steuerlich abzugsfähig und senken deine Rücklage – plane sie bewusst ein." },
      },
      {
        id: "mwst-grenze", tocLabel: "MWST-Grenze",
        title: "Mehrwertsteuer: Ab CHF 100'000 Umsatz wird es Pflicht",
        paragraphs: [
          <>Unter <strong>CHF 100'000 Jahresumsatz</strong> bist du von der MWST befreit – deine Rechnungen enthalten keine Steuer, du legst dafür auch nichts zurück. Sobald du die Grenze überschreitest (oder freiwillig registrierst), musst du den Normalsatz von 8,1 % verrechnen und quartalsweise oder halbjährlich mit der ESTV abrechnen.</>,
          <>Für viele Freelancer lohnt sich die <strong>Saldosteuersatz-Methode</strong>: Du rechnest mit einem branchenspezifischen Pauschalsatz auf den Umsatz ab und sparst dir die Vorsteuer-Buchhaltung. Mit Swiss QR-Bill auf jeder Rechnung landet der MWST-Betrag automatisch korrekt beim Kunden.</>,
        ],
      },
    ],
    ivoliqTitle: "Bundessteuer, Kantonssteuer, AHV – live aus deinen QR-Rechnungen",
    ivoliqSubtitle: "ivoliq rechnet für deinen Kanton und zeigt dir nach jeder Rechnung, was du zurücklegen musst.",
    mistakesTitle: "Die 4 häufigsten Rücklagen-Fehler in der Schweiz",
    ivoliqSteps: [
      { title: "QR-Rechnung erstellen", description: "Erstelle deine Rechnung mit Swiss QR-Bill. Die App berechnet sofort deine Schweizer Steuern." },
      { title: "Automatische Berechnung", description: "ivoliq berechnet live: Bundessteuer, Kantonssteuer, AHV – alles für deinen Kanton." },
      { title: "Rücklagen-Dashboard", description: "Sieh auf einen Blick: Wie viel musst du zurücklegen? Wann kommen AHV-Nachzahlungen?" },
    ],
    ivoliqFeatures: ["Automatische Berechnung nach Schweizer Steuerrecht", "AHV-Beiträge kalkuliert", "Swiss QR-Bill auf jeder Rechnung", "Export für Treuhänder"],
    vorauszahlungTitle: "Steuervorauszahlungen in der Schweiz",
    vorauszahlungIntro: [
      "In der Schweiz variieren die Steuertermine je nach Kanton. Die meisten Kantone fordern provisorische Steuerzahlungen (Akonto-Zahlungen) im Laufe des Jahres.",
      <><strong>AHV-Beiträge:</strong> Werden quartalsweise oder jährlich abgerechnet, basierend auf dem Vorjahreseinkommen. Die definitive Abrechnung erfolgt rückwirkend.</>,
    ],
    vorauszahlungQuarters: [
      { label: "1", date: "Akonto-Rechnung", note: "kantonal, meist im Frühjahr" },
      { label: "2", date: "AHV-Akonto", note: "quartalsweise Ausgleichskasse" },
      { label: "3", date: "Steuererklärung", note: "Frist je Kanton, meist 31. März" },
      { label: "4", date: "Schlussrechnung", note: "nach definitiver Veranlagung" },
    ],
    donts: [
      { title: "Keine Rücklagen bilden", desc: "Steuernachzahlungen können in der Schweiz hoch ausfallen" },
      { title: "Kantonale Unterschiede ignorieren", desc: "Steuersätze variieren je nach Kanton um bis zu 100%" },
      { title: "AHV-Beiträge vergessen", desc: "AHV-Nachzahlungen können mehrere tausend CHF betragen" },
      { title: "MWST-Grenze überschreiten", desc: "Ab CHF 100'000 Umsatz bist du MWST-pflichtig" },
    ],
    dos: [
      { title: "25-40% automatisch berechnen", desc: "Mit ivoliq siehst du nach jeder Rechnung die nötigen Rücklagen" },
      { title: "Kanton berücksichtigen", desc: "Steuersätze je nach Wohnkanton korrekt einberechnen" },
      { title: "AHV-Beiträge einplanen", desc: "Quartalsweise AHV-Beiträge einkalkulieren" },
      { title: "Treuhänder für Steuererklärung", desc: "Laufende Buchhaltung mit ivoliq, Steuererklärung vom Profi" },
    ],
    faqTitle: "Häufig gestellte Fragen zu Steuerrücklagen in der Schweiz",
    faqs: [
      { question: "Wie viel Steuern muss ich als Freelancer in der Schweiz zahlen?", answer: "25-40% des Gewinns, je nach Kanton. Das umfasst Bundessteuer (0-11,5%), Kantonssteuer (5-35%) und AHV (~10,6%)." },
      { question: "Warum sind die Steuern je nach Kanton so unterschiedlich?", answer: "In der Schweiz haben Kantone und Gemeinden eigene Steuersätze. Zürich ist teurer als Zug, Schwyz günstiger als Genf." },
      { question: "Muss ich als Freelancer AHV-Beiträge zahlen?", answer: "Ja, als Selbständigerwerbende/r bist du AHV-pflichtig (~10,6% des Reineinkommens)." },
      { question: "Ab wann bin ich MWST-pflichtig?", answer: "Ab CHF 100'000 Jahresumsatz. Darunter kannst du dich freiwillig registrieren." },
      { question: "Was bedeutet Akonto-Zahlung bei der AHV?", answer: "Die Ausgleichskasse erhebt AHV/IV/EO-Beiträge zunächst provisorisch auf Basis deines geschätzten Einkommens. Nach der Steuerveranlagung folgt die definitive Abrechnung – bei zu tiefer Schätzung mit Nachzahlung und Verzugszins." },
      { question: "Hilft ivoliq bei kantonalen Steuern?", answer: "ivoliq berechnet Bundessteuer und gibt eine Schätzung der Kantonssteuer. Für die exakte kantonale Steuererklärung empfehlen wir deinen Treuhänder." },
    ],
    relatedCalculators: [
      { to: "/steuerruecklagen-freelancer", label: "Steuerrücklagen Übersicht" },
      { to: "/steuerruecklagen-freelancer-deutschland", label: "Steuerrücklagen Deutschland" },
      { to: "/steuerruecklagen-freelancer-oesterreich", label: "Steuerrücklagen Österreich" },
    ],
    relatedPages: [
      { to: "/buchhaltung-freelancer-schweiz", label: "Buchhaltung Schweiz" },
      { to: "/rechnungsvorlage-freelancer", label: "Rechnungsvorlagen" },
    ],
    finalCtaTitle: "Nie wieder Steuerschock in der Schweiz",
    finalCtaSubtitle: "Berechne deine Steuerrücklagen automatisch mit ivoliq – nach Schweizer Steuerrecht.",
    finalTrustLine: (year) => `✓ Schweiz-optimiert • ✓ Swiss QR-Bill • ✓ Aktuelle Steuersätze ${year}`,
    finalTrustBadges: [
      { label: "Swiss QR-Bill", icon: "QrCode" },
      { label: "Schweiz-optimiert", icon: "Shield" },
    ],
    finalTrustBadgeDynamic: (year) => ({ label: `Aktuelle Steuersätze ${year}`, icon: "Calculator" }),
  },
};

// Swiss cantons for calculator dropdown
export const SWISS_CANTONS = [
  { code: "ZH", name: "Zürich" }, { code: "BE", name: "Bern" }, { code: "LU", name: "Luzern" },
  { code: "UR", name: "Uri" }, { code: "SZ", name: "Schwyz" }, { code: "OW", name: "Obwalden" },
  { code: "NW", name: "Nidwalden" }, { code: "GL", name: "Glarus" }, { code: "ZG", name: "Zug" },
  { code: "FR", name: "Freiburg" }, { code: "SO", name: "Solothurn" }, { code: "BS", name: "Basel-Stadt" },
  { code: "BL", name: "Basel-Landschaft" }, { code: "SH", name: "Schaffhausen" },
  { code: "AR", name: "Appenzell Ausserrhoden" }, { code: "AI", name: "Appenzell Innerrhoden" },
  { code: "SG", name: "St. Gallen" }, { code: "GR", name: "Graubünden" }, { code: "AG", name: "Aargau" },
  { code: "TG", name: "Thurgau" }, { code: "TI", name: "Tessin" }, { code: "VD", name: "Waadt" },
  { code: "VS", name: "Wallis" }, { code: "NE", name: "Neuenburg" }, { code: "GE", name: "Genf" },
  { code: "JU", name: "Jura" },
];
